import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { KpiCard, Section } from '../../components/ui'
import type { AdminSystemHealth as AdminSystemHealthType } from '../../lib/types'

export default function AdminSystemHealth() {
  const [data, setData] = useState<AdminSystemHealthType | null>(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    setData(await api('/admin/system-health'))
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  if (loading && !data) return <div className="page-loading">Checking system health…</div>
  if (!data) return <div className="empty">Could not load system health.</div>

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1>System health</h1>
          <div className="ph-sub">Checked {new Date(data.checked_at).toLocaleString()}</div>
        </div>
        <button className="btn small outline" onClick={load}>Refresh</button>
      </div>

      <div className="kpis">
        <KpiCard label="Backend" value={<span className={`badge ${data.backend.status === 'ok' ? 'badge-green' : 'badge-red'}`}>{data.backend.status}</span>} />
        <KpiCard label="Neon connectivity" value={<span className={`badge ${data.database.connected ? 'badge-green' : 'badge-red'}`}>{data.database.connected ? 'Connected' : 'Down'}</span>} />
        <KpiCard label="Database latency" value={`${data.database.latency_ms} ms`} />
        <KpiCard label="API latency (self)" value={`${data.api_latency_ms} ms`} />
        <KpiCard label="Alembic version" value={data.alembic_version || '—'} />
        <KpiCard label="Environment" value={data.environment} />
      </div>

      {data.database.error && (
        <div className="form-error" style={{ marginTop: 10 }}>Database error: {data.database.error}</div>
      )}

      <Section title="Repository">
        <div className="kpis">
          <KpiCard label="Commit hash" value={data.repository.commit_hash || 'unavailable'} />
          <KpiCard label="Commit date" value={data.repository.commit_date ? new Date(data.repository.commit_date).toLocaleString() : 'unavailable'} />
        </div>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Frontend and backend ship from the same monorepo commit, so a single hash covers both. There is no
          recorded deployment commit hash — this phase does not deploy.
        </p>
      </Section>

      <Section title="Firebase Hosting configuration" note="local detection only — not a live deploy status">
        <div className="kpis">
          <KpiCard label="Configured" value={data.firebase_hosting.configured ? 'Yes' : 'No'} />
          <KpiCard label="Project ID" value={data.firebase_hosting.project_id || '—'} />
          <KpiCard label="Hosting public dir" value={data.firebase_hosting.hosting_public_dir || '—'} />
        </div>
      </Section>

      <Section title="Pricing configuration">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Plan</th><th>Seat fee</th><th>Outcome fee</th></tr></thead>
            <tbody>
              {Object.entries(data.pricing_tiers).map(([key, tier]) => (
                <tr key={key}>
                  <td>{tier.label}</td>
                  <td>{tier.seat_fee_cents != null ? `$${(tier.seat_fee_cents / 100).toFixed(2)}` : 'Contact sales'}</td>
                  <td>{tier.outcome_fee_cents != null ? `$${(tier.outcome_fee_cents / 100).toFixed(2)}` : 'Contact sales'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Recent errors">
        <div className="row-list">
          {data.recent_errors.map((err, i) => <div key={i} className="row">{err}</div>)}
          {data.recent_errors.length === 0 && <div className="empty">No error log is configured yet — nothing to show.</div>}
        </div>
      </Section>
    </div>
  )
}
